import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./chooseride.css";

const eatingQuestions = [
  "What do you want to eat?",
  "Wetin you won chop?", // Pidgin
  "Kí ni o fẹ́ jẹ?", // Yoruba
  "Kedu ihe ị chọrọ iri?", // Igbo
  "Me kake so ka ci?", // Hausa
];

const locationTypes = [
  { name: "House", icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg> },
  { name: "Apartment", icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg> },
  { name: "Office", icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg> },
  { name: "Others", icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg> }
];

function FoodRide({ isModal = false }) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [address, setAddress] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationType, setLocationType] = useState("");
  const autocompleteService = useRef(null);
  const geocoder = useRef(null);
  const mapRef = useRef(null);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [storedAddress, setStoredAddress] = useState("");
  const [storedLocationType, setStoredLocationType] = useState("");
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentQuestionIndex((prevIndex) => (prevIndex + 1) % eatingQuestions.length);
    }, 3000); // Changes the text every 3 seconds

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, []);

  useEffect(() => {
    setStoredAddress(localStorage.getItem('9troride_address') || '');
    setStoredLocationType(localStorage.getItem('9troride_locationType') || '');
  }, []);

  // Initialize Google Map in the modal when opened
  useEffect(() => {
    if (isModalOpen && mapRef.current && selectedLocation && window.google && window.google.maps) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: selectedLocation,
        zoom: 16,
        disableDefaultUI: true,
        zoomControl: false,
        gestureHandling: "none",
        keyboardShortcuts: false,
      });

      const customIcon = {
        path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
        fillColor: "#E3242B",
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: "#ffffff",
        scale: 1.8,
        anchor: new window.google.maps.Point(12, 24),
      };

      const marker = new window.google.maps.Marker({
        position: selectedLocation,
        map: map,
        icon: customIcon,
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `<div style="padding: 4px; text-align: center; max-width: 200px; font-family: 'Plus Jakarta Sans', sans-serif;"><div style="font-weight: 600; font-size: 14px; color: #111; margin-bottom: 6px;">${address}</div><div style="font-size: 12px; color: #666; line-height: 1.4;">We are going to deliver your food here.</div></div>`,
      });

      infoWindow.open(map, marker);
    }
  }, [isModalOpen, selectedLocation, address]);

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setAddress(value);

    if (!value) {
      setPredictions([]);
      return;
    }

    // Make sure Google Maps API is loaded in your index.html
    if (window.google && window.google.maps && window.google.maps.places) {
      if (!autocompleteService.current) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }

      autocompleteService.current.getPlacePredictions(
        { input: value },
        (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            setPredictions(results);
          } else {
            setPredictions([]);
          }
        }
      );
    }
  };

  const handlePredictionSelect = (prediction) => {
    setAddress(prediction.description);
    setPredictions([]);

    if (window.google && window.google.maps) {
      if (!geocoder.current) {
        geocoder.current = new window.google.maps.Geocoder();
      }
      geocoder.current.geocode({ placeId: prediction.place_id }, (results, status) => {
        if (status === "OK" && results[0]) {
          setSelectedLocation({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          });
          setIsModalOpen(true);
        }
      });
    }
  };

  const handleCurrentLocation = () => {
    setLocationError(null);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          if (window.google && window.google.maps) {
            if (!geocoder.current) {
              geocoder.current = new window.google.maps.Geocoder();
            }

            // Reverse geocode the coordinates to a human-readable address
            geocoder.current.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === "OK" && results[0]) {
                setAddress(results[0].formatted_address);
              } else {
                setAddress(`${lat}, ${lng}`);
              }
            });
          } else {
            setAddress(`${lat}, ${lng}`); // Fallback to coordinates
          }

          setSelectedLocation({ lat, lng });
          setIsModalOpen(true);
        },
        (error) => {
          console.error("Error getting current location:", error);
          setLocationError("Unable to retrieve your location. Please enter your address manually.");
        }
      );
    } else {
      setLocationError("Geolocation is not supported by your browser.");
    }
  };

  const handleConfirmLocation = () => {
    setIsModalOpen(false);
    localStorage.setItem('9troride_address', address);
    localStorage.setItem('9troride_locationType', locationType);
    if (selectedLocation) {
      localStorage.setItem('9troride_selectedLocation', JSON.stringify(selectedLocation));
    }
    navigate("/9troeat/vendors", { state: { address, locationType, selectedLocation } });
  };

  const handleSearch = () => {
    navigate("/9troeat/vendors", { 
      state: { 
        address: storedAddress, 
        locationType: storedLocationType,
        searchQuery: searchQuery 
      } 
    });
  };

  return (
    <div className={`chooseride foodride ${isModal ? 'in-modal' : ''}`}>
      <div className='chooseride-container'>
        {locationError && (
          <div className="location-error-notice">
            <p>{locationError}</p>
            <button onClick={() => setLocationError(null)}>×</button>
          </div>
        )}
        <div className='chooseride-title' style={{ width: "100%", alignItems: "center" }}>
          <h1 className='foodrideh1' style={{ transition: "opacity 0.5s ease-in-out" }}>
            {eatingQuestions[currentQuestionIndex]}
          </h1>
          <div className='address-search-wrapper'>
            {storedAddress ? (
              <div className='address-search-bar'>
                <div className="address-input-container">
                  <input 
                    type="text" 
                    className="address-search-input" 
                    placeholder="What do you want to eat?" 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  {searchQuery && (
                    <button className="clear-address-btn" onClick={() => setSearchQuery("")}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  )}
                </div>
                <button className="current-location-btn" onClick={handleSearch}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                  Search
                </button>
              </div>
            ) : (
              <>
                <div className='address-search-bar'>
                  <div className="address-input-container">
                    <input type="text" className="address-search-input" placeholder="What's your address?" value={address} onChange={handleAddressChange} />
                    {address && (
                      <button className="clear-address-btn" onClick={() => { setAddress(""); setPredictions([]); }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    )}
                  </div>
                  <button className="current-location-btn" onClick={handleCurrentLocation}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    Use current location
                  </button>
                </div>
                {predictions.length > 0 && (
                  <ul className="predictions-dropdown">
                    {predictions.map((p) => (
                      <li key={p.place_id} className="prediction-item" onClick={() => handlePredictionSelect(p)}>
                        {p.description}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="location-modal-overlay">
          <div className="location-modal">
            <div className="modal-header">
              <h2>Delivery details</h2>
              <button className="close-modal-btn" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            
            <div className="map-container" ref={mapRef}></div>
            
            <div className="modal-content-body">
              <div className="location-type-selection">
                <h3>Add a label</h3>
                <div className="type-buttons">
                  {locationTypes.map((type) => (
                    <button 
                      key={type.name}
                      className={`type-btn ${locationType === type.name ? 'active' : ''}`}
                      onClick={() => setLocationType(type.name)}
                    >
                      {type.icon}
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>

              <button 
                className="confirm-location-btn" 
                onClick={handleConfirmLocation}
                disabled={!locationType}
              >
                Confirm & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FoodRide;
